if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
      }

      connectedCallback() {
        super.connectedCallback();
        this.initializeProductLightbox();
      }

      hide() {
        const activeMediaId = this.activeMediaId;
        const sectionId = this.dataset.section;

        this.resetImageZoom();
        this.updateMobileBuyButtonVisibility(false);
        super.hide();

        document.dispatchEvent(
          new CustomEvent('product:media-modal-closed', {
            detail: {
              mediaId: activeMediaId,
              sectionId,
            },
          })
        );
      }

      show(opener) {
        super.show(opener);
        this.setActiveMedia(opener.getAttribute('data-media-id'));
        this.updateMobileBuyButtonVisibility(true);
      }

      showActiveMedia() {
        this.setActiveMedia(this.openedBy.getAttribute('data-media-id'));
      }

      initializeProductLightbox() {
        if (this.productLightboxInitialized) return;
        this.productLightboxInitialized = true;

        this.querySelectorAll('[data-product-media-modal-keep-open]').forEach((element) => {
          element.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.querySelectorAll('[data-product-media-modal-thumbnail]').forEach((button) => {
          button.addEventListener('click', () => this.setActiveMedia(button.getAttribute('data-media-id')));
        });

        this.querySelector('[data-product-media-modal-previous]')?.addEventListener('click', () => {
          this.showAdjacentMedia(-1);
        });

        this.querySelector('[data-product-media-modal-next]')?.addEventListener('click', () => {
          this.showAdjacentMedia(1);
        });

        const viewer = this.querySelector('.product-media-modal__viewer');

        viewer?.addEventListener('click', (event) => {
          if (this.suppressNextImageClick) {
            this.suppressNextImageClick = false;
            return;
          }

          const image = event.target.closest('.product-media-modal__viewer > img.active');
          if (!image) return;

          this.toggleImageZoom(image, event);
        });

        viewer?.addEventListener('pointerdown', (event) => this.handleViewerPointerDown(event));
        viewer?.addEventListener('pointermove', (event) => this.handleViewerPointerMove(event));
        viewer?.addEventListener('pointerup', (event) => this.handleViewerPointerEnd(event));
        viewer?.addEventListener('pointercancel', (event) => this.handleViewerPointerEnd(event));

        viewer?.addEventListener('pointermove', (event) => {
          if (this.isMobileLightbox() || this.imagePanState || this.mediaSwipeState) return;

          const image = this.querySelector('.product-media-modal__viewer > img.active.is-zoomed');
          if (!image) return;

          this.updateImageZoomPosition(image, event);
        });

        window.addEventListener('resize', () => this.updateArrowPositions());

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.dataset.section) return;
            this.updateMobileBuyButtonVariant(event.data.variant);
          });
        }

        this.updateControls();
      }

      get mediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__viewer > [data-media-id]'));
      }

      setActiveMedia(mediaId) {
        if (!mediaId) return;

        const activeMedia = this.mediaItems.find((element) => element.getAttribute('data-media-id') === mediaId);
        if (!activeMedia) return;

        this.resetImageZoom();
        this.activeMediaId = mediaId;
        window.pauseAllMedia();

        this.mediaItems.forEach((element) => {
          element.classList.toggle('active', element === activeMedia);
        });

        this.querySelectorAll('[data-product-media-modal-thumbnail]').forEach((thumbnail) => {
          const isActive = thumbnail.getAttribute('data-media-id') === mediaId;
          thumbnail.classList.toggle('active', isActive);
          thumbnail.toggleAttribute('aria-current', isActive);

          if (isActive) {
            thumbnail.scrollIntoView({ block: 'nearest', inline: 'nearest' });
          }
        });

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        const deferredMedia = activeMedia.matches('deferred-media')
          ? activeMedia
          : activeMedia.querySelector('deferred-media');

        if (
          deferredMedia &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          deferredMedia.loadContent();

        this.updateArrowPositionsWhenReady(activeMedia);
      }

      toggleImageZoom(image, event) {
        const willZoom = !image.classList.contains('is-zoomed');
        this.resetImageZoom();

        if (!willZoom) return;

        image.classList.add('is-zoomed');
        if (this.isMobileLightbox()) {
          image.style.setProperty('--zoom-origin-x', '50%');
          image.style.setProperty('--zoom-origin-y', '50%');
          this.setImagePan(image, 0, 0);
        } else {
          this.updateImageZoomPosition(image, event);
        }
      }

      updateImageZoomPosition(image, event) {
        const bounds = image.getBoundingClientRect();
        const x = ((event.clientX - bounds.left) / bounds.width) * 100;
        const y = ((event.clientY - bounds.top) / bounds.height) * 100;

        image.style.setProperty('--zoom-origin-x', `${Math.min(Math.max(x, 0), 100)}%`);
        image.style.setProperty('--zoom-origin-y', `${Math.min(Math.max(y, 0), 100)}%`);
      }

      resetImageZoom() {
        this.querySelectorAll('.product-media-modal__viewer > img.is-zoomed').forEach((image) => {
          image.classList.remove('is-zoomed');
          image.classList.remove('is-panning');
          image.style.removeProperty('--zoom-origin-x');
          image.style.removeProperty('--zoom-origin-y');
          image.style.removeProperty('--zoom-pan-x');
          image.style.removeProperty('--zoom-pan-y');
          delete image.dataset.zoomPanX;
          delete image.dataset.zoomPanY;
        });
        this.imagePanState = null;
        this.mediaSwipeState = null;
      }

      isMobileLightbox() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      handleViewerPointerDown(event) {
        if (!this.isMobileLightbox()) return;

        const image = event.target.closest('.product-media-modal__viewer > img.active');
        if (!image) return;

        if (image.classList.contains('is-zoomed')) {
          this.startImagePan(image, event);
          return;
        }

        this.mediaSwipeState = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          currentX: event.clientX,
          currentY: event.clientY,
        };
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }

      handleViewerPointerMove(event) {
        if (this.imagePanState) {
          this.updateImagePan(event);
          return;
        }

        if (!this.mediaSwipeState || this.mediaSwipeState.pointerId !== event.pointerId) return;

        this.mediaSwipeState.currentX = event.clientX;
        this.mediaSwipeState.currentY = event.clientY;
      }

      handleViewerPointerEnd(event) {
        if (this.imagePanState) {
          this.endImagePan(event);
          return;
        }

        if (!this.mediaSwipeState || this.mediaSwipeState.pointerId !== event.pointerId) return;

        const deltaX = this.mediaSwipeState.currentX - this.mediaSwipeState.startX;
        const deltaY = this.mediaSwipeState.currentY - this.mediaSwipeState.startY;
        const isSwipe = Math.abs(deltaX) > 48 && Math.abs(deltaX) > Math.abs(deltaY) * 1.25;
        this.mediaSwipeState = null;

        if (!isSwipe) return;

        this.suppressNextImageClick = true;
        this.showAdjacentMedia(deltaX < 0 ? 1 : -1);
      }

      startImagePan(image, event) {
        this.imagePanState = {
          image,
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          originX: Number(image.dataset.zoomPanX || 0),
          originY: Number(image.dataset.zoomPanY || 0),
          moved: false,
        };
        image.classList.add('is-panning');
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      }

      updateImagePan(event) {
        const state = this.imagePanState;
        if (!state || state.pointerId !== event.pointerId) return;

        const deltaX = event.clientX - state.startX;
        const deltaY = event.clientY - state.startY;
        if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) state.moved = true;

        const pan = this.clampImagePan(state.image, state.originX + deltaX, state.originY + deltaY);
        this.setImagePan(state.image, pan.x, pan.y);
        event.preventDefault();
      }

      endImagePan(event) {
        const state = this.imagePanState;
        if (!state || state.pointerId !== event.pointerId) return;

        state.image.classList.remove('is-panning');
        this.suppressNextImageClick = state.moved;
        this.imagePanState = null;
      }

      setImagePan(image, x, y) {
        image.dataset.zoomPanX = String(x);
        image.dataset.zoomPanY = String(y);
        image.style.setProperty('--zoom-pan-x', `${x}px`);
        image.style.setProperty('--zoom-pan-y', `${y}px`);
      }

      clampImagePan(image, x, y) {
        const scale = Number.parseFloat(getComputedStyle(image).getPropertyValue('--zoom-scale')) || 1.85;
        const maxX = Math.max(0, ((image.clientWidth || image.getBoundingClientRect().width) * (scale - 1)) / 2);
        const maxY = Math.max(0, ((image.clientHeight || image.getBoundingClientRect().height) * (scale - 1)) / 2);

        return {
          x: Math.min(Math.max(x, -maxX), maxX),
          y: Math.min(Math.max(y, -maxY), maxY),
        };
      }

      showAdjacentMedia(direction) {
        const mediaItems = this.mediaItems;
        if (mediaItems.length < 2) return;

        const currentIndex = mediaItems.findIndex((element) => element.getAttribute('data-media-id') === this.activeMediaId);
        const nextIndex = (currentIndex + direction + mediaItems.length) % mediaItems.length;
        this.setActiveMedia(mediaItems[nextIndex].getAttribute('data-media-id'));
      }

      updateControls() {
        const isSingleMedia = this.mediaItems.length < 2;

        this.querySelectorAll('[data-product-media-modal-previous], [data-product-media-modal-next]').forEach((button) => {
          button.hidden = isSingleMedia;
        });
      }

      updateMobileBuyButtonVisibility(visible = this.hasAttribute('open')) {
        const container = this.querySelector('[data-product-media-modal-buy-button]');
        if (!container) return;

        const isMobile = window.matchMedia('(max-width: 749px)').matches;
        const mainVariantInput = document.querySelector(`#product-form-${this.dataset.section} input[name="id"]`);
        const shouldShow = visible && isMobile && !mainVariantInput?.disabled;

        container.hidden = !shouldShow;

        if (shouldShow) {
          window.Shopify?.PaymentButton?.init();
        }
      }

      updateMobileBuyButtonVariant(variant) {
        const variantInput = this.querySelector('[data-product-media-modal-buy-button] input[name="id"]');
        if (!variantInput) return;

        variantInput.value = variant?.id ?? '';
        variantInput.disabled = !variant?.available;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.updateMobileBuyButtonVisibility();
      }

      updateArrowPositionsWhenReady(activeMedia) {
        requestAnimationFrame(() => this.updateArrowPositions());

        if (activeMedia?.matches('img') && !activeMedia.complete) {
          activeMedia.addEventListener('load', () => this.updateArrowPositions(), { once: true });
        }
      }

      updateArrowPositions() {
        const frame = this.querySelector('.product-media-modal__frame');
        const activeMedia = this.mediaItems.find((element) => element.classList.contains('active'));

        if (!frame || !activeMedia || window.matchMedia('(max-width: 989px)').matches) {
          frame?.style.removeProperty('--product-media-modal-arrow-previous-x');
          frame?.style.removeProperty('--product-media-modal-arrow-next-x');
          return;
        }

        const frameRect = frame.getBoundingClientRect();
        const activeMediaRect = this.getActiveMediaVisualRect(activeMedia);
        const frameStyles = window.getComputedStyle(frame);
        const arrowOffset = parseFloat(frameStyles.getPropertyValue('--product-media-modal-arrow-offset')) || 84;
        const arrowHalfWidth = 24;
        const previousX = activeMediaRect.left - frameRect.left - arrowOffset;
        const nextX = activeMediaRect.right - frameRect.left + arrowOffset;

        frame.style.setProperty(
          '--product-media-modal-arrow-previous-x',
          `${Math.max(arrowHalfWidth, previousX)}px`
        );
        frame.style.setProperty(
          '--product-media-modal-arrow-next-x',
          `${Math.min(frameRect.width - arrowHalfWidth, nextX)}px`
        );
      }

      getActiveMediaVisualRect(activeMedia) {
        const rect = activeMedia.getBoundingClientRect();

        if (!activeMedia.matches('img') || !activeMedia.naturalWidth || !activeMedia.naturalHeight) return rect;

        const mediaAspectRatio = activeMedia.naturalWidth / activeMedia.naturalHeight;
        const containerAspectRatio = rect.width / rect.height;
        let width = rect.width;
        let height = rect.height;

        if (containerAspectRatio > mediaAspectRatio) {
          width = height * mediaAspectRatio;
        } else {
          height = width / mediaAspectRatio;
        }

        const left = rect.left + (rect.width - width) / 2;
        const top = rect.top + (rect.height - height) / 2;

        return {
          left,
          right: left + width,
          top,
          bottom: top + height,
          width,
          height,
        };
      }
    }
  );
}
